import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/homeData';

export default function Hero() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setDropdownOpen(false);
      }
      if (mobileMenuOpen) {
        const isInsideMenu = mobileMenuRef.current?.contains(target);
        const isHamburger = hamburgerRef.current?.contains(target);
        if (!isInsideMenu && !isHamburger) {
          setMobileMenuOpen(false);
        }
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileMenuOpen]);

  const handleCategoryClick = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    setDropdownOpen(false);
    setMobileMenuOpen(false);
  };

  return (
    <section className="relative w-full h-[600px] md:h-[680px] flex flex-col overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Warm%20editorial%20lifestyle%20flat%20lay%20with%20soft%20diffused%20natural%20light%2C%20an%20arrangement%20of%20fashion%20accessories%20including%20a%20pair%20of%20caramel%20brown%20leather%20sneakers%2C%20a%20cozy%20oatmeal%20knit%20sweater%20draped%20casually%2C%20a%20minimalist%20cream%20tote%20bag%2C%20and%20a%20small%20ceramic%20vase%20with%20dried%20pampas%20grass%2C%20all%20arranged%20on%20a%20light%20oak%20parquet%20floor%2C%20gentle%20morning%20sun%20casting%20soft%20elongated%20shadows%2C%20sophisticated%20neutral%20palette%20of%20cream%20beige%20warm%20brown%20and%20soft%20ivory%2C%20cozy%20aspirational%20home%20aesthetic%20with%20organic%20composition&width=1600&height=900&seq=hero-bg-v4-x2&orientation=landscape"
          alt="CSSBuy Spreadsheet Hub background"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-black/25"></div>
      </div>

      <nav className="relative z-10 w-full px-6 md:px-14 py-5 flex items-center justify-between">
        <Link
          to="/"
          className="text-white font-heading text-xl md:text-2xl font-bold tracking-tight whitespace-nowrap cursor-pointer"
        >
          cssspreadsheet<span className="text-primary-300">.</span>makeup
        </Link>

        <div className="hidden md:flex items-center gap-5">
          <Link
            to="/"
            className="text-white/80 text-sm font-medium hover:text-white transition-colors duration-200 whitespace-nowrap cursor-pointer flex items-center gap-1.5"
          >
            <i className="ri-home-4-line"></i>
            Home
          </Link>
          <Link
            to="/article/cssbuy-spreadsheet-2026-guide"
            className="text-white/80 text-sm font-medium hover:text-white transition-colors duration-200 whitespace-nowrap cursor-pointer flex items-center gap-1.5"
          >
            <i className="ri-book-open-line"></i>
            Shopping Guide
          </Link>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="bg-white/20 backdrop-blur-sm text-white text-sm font-semibold px-6 py-2.5 rounded-full border border-white/30 hover:bg-white/30 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            Buy It Now
          </a>
        </div>

        <button
          ref={hamburgerRef}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden text-white p-2 cursor-pointer"
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? (
            <i className="ri-close-line text-2xl"></i>
          ) : (
            <i className="ri-menu-line text-2xl"></i>
          )}
        </button>
      </nav>

      {mobileMenuOpen && (
        <div
          ref={mobileMenuRef}
          className="md:hidden fixed top-[72px] left-4 right-4 z-50 bg-white shadow-xl rounded-2xl overflow-hidden max-h-[calc(100vh-100px)] overflow-y-auto"
        >
          <div className="p-4 space-y-1">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-home-4-line text-primary-500 text-lg"></i>
              Home
            </Link>
            <Link
              to="/article/cssbuy-spreadsheet-2026-guide"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-book-open-line text-primary-500 text-lg"></i>
              Shopping Guide
            </Link>
            <div className="border-t border-background-200 my-2"></div>
            <p className="px-4 py-2 text-xs font-semibold text-foreground-400 uppercase tracking-widest">Categories</p>
            {categories.map((cat, idx) => (
              <button
                key={idx}
                onClick={() => handleCategoryClick(cat.url)}
                className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0">
                  <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                </div>
                <span>{cat.name}</span>
                <i className="ri-external-link-line text-foreground-400 text-xs ml-auto"></i>
              </button>
            ))}
            <div className="border-t border-background-200 my-2"></div>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold bg-primary-500 text-white hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-shopping-bag-3-line text-lg"></i>
              Buy It Now
            </a>
          </div>
        </div>
      )}

      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-4 md:px-6 text-center w-full">
        <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white max-w-4xl leading-tight">
          CSSBuy Spreadsheet Hub
        </h1>
        <p className="font-heading text-2xl sm:text-3xl md:text-4xl font-light text-white/90 mt-2">
          The Most Complete Database — 2026 Updated
        </p>
        <p className="text-white/70 text-sm md:text-base max-w-2xl mt-5 leading-relaxed">
          Discover verified CSSBuy, Kakobuy, Superbuy &amp; Oopbuy spreadsheets with real QC data,
          price comparisons, and daily updated shopping lists.
        </p>

        <div className="mt-8 flex flex-col sm:flex-row items-center gap-4">
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-3 bg-white/25 backdrop-blur-sm text-white border border-white/30 rounded-full px-6 py-3 text-sm font-semibold hover:bg-white/35 transition-all duration-200 whitespace-nowrap cursor-pointer min-w-[240px] justify-between"
            >
              <span>Browse Categories</span>
              {dropdownOpen ? (
                <i className="ri-arrow-up-s-line text-base transition-transform duration-200"></i>
              ) : (
                <i className="ri-arrow-down-s-line text-base transition-transform duration-200"></i>
              )}
            </button>
            {dropdownOpen && (
              <div className="absolute top-full mt-2 left-0 right-0 bg-white rounded-2xl overflow-hidden z-50 max-h-[360px] overflow-y-auto">
                {categories.map((cat, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleCategoryClick(cat.url)}
                    className="w-full text-left px-5 py-3 text-sm text-foreground-800 hover:bg-background-100 transition-colors duration-150 flex items-center justify-between cursor-pointer whitespace-nowrap"
                  >
                    <span>{cat.name}</span>
                    <i className="ri-external-link-line text-foreground-400 text-xs"></i>
                  </button>
                ))}
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="bg-white text-foreground-900 font-semibold px-8 py-3 rounded-full text-sm hover:bg-background-100 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            CSSBuy Sheet
          </a>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="bg-primary-500 text-white font-semibold px-8 py-3 rounded-full text-sm hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            Buy It Now
          </a>
        </div>

        <div className="mt-8 flex items-center gap-6 md:gap-8 text-white/60 text-xs md:text-sm">
          <span className="flex items-center gap-1.5">
            <i className="ri-checkbox-circle-line text-accent-400"></i> 1000+ Verified Items
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-refresh-line text-accent-400"></i> Updated Daily
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-shield-check-line text-accent-400"></i> Multi-Agent Coverage
          </span>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-s-line text-white/50 text-2xl"></i>
        </div>
      </div>
    </section>
  );
}